import React, { useMemo, useState } from "react";
import { BackButton, Button, Card, Chip, HeartIcon, MapPinIcon, SectionTitle } from "./ui";

/* -------------------------------------------------------------------------- */
/* Explore — list of compatible dogs with filters and quick actions.          */
/* -------------------------------------------------------------------------- */

const sexOptions = ["Todos", "Hembra", "Macho"];
const sizeOptions = ["Todos", "Pequeño", "Mediano", "Grande"];

const distanceBuckets = [
  { key: "any", label: "Cualquier distancia", max: Infinity },
  { key: "5", label: "Hasta 5 km", max: 5 },
  { key: "10", label: "Hasta 10 km", max: 10 },
];

const parseKm = (s) => {
  if (!s) return 999;
  const n = parseFloat(String(s).replace(/[^\d.]/g, ""));
  return isNaN(n) ? 999 : n;
};

export default function Explore({
  dogs,
  hasDogs = true,
  onAddDog,
  onOpenDog,
  onSendRequest,
  sentRequests,
  onBack,
  myDogName,
  locationLabel = "",
}) {
  const [query, setQuery] = useState("");
  const [sex, setSex] = useState("Todos");
  const [size, setSize] = useState("Todos");
  const [distance, setDistance] = useState("any");
  const [vaccinesOnly, setVaccinesOnly] = useState(false);
  const [pedigreeOnly, setPedigreeOnly] = useState(false);
  const [availableOnly, setAvailableOnly] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    const maxKm =
      distanceBuckets.find((b) => b.key === distance)?.max ?? Infinity;
    const q = query.trim().toLowerCase();
    return dogs.filter((d) => {
      if (q && !`${d.name} ${d.breed}`.toLowerCase().includes(q)) return false;
      if (sex !== "Todos" && d.sex !== sex) return false;
      if (size !== "Todos" && d.size !== size) return false;
      if (parseKm(d.distance) > maxKm) return false;
      if (vaccinesOnly && !d.vaccines) return false;
      if (pedigreeOnly && d.pedigree !== "Sí") return false;
      if (availableOnly && d.available === false) return false;
      return true;
    });
  }, [dogs, query, sex, size, distance, vaccinesOnly, pedigreeOnly, availableOnly]);

  return (
    <div className="mx-auto max-w-5xl px-4 pb-28 pt-4 sm:px-6" data-testid="explore-screen">
      <div className="mb-4 flex items-center justify-between">
        <BackButton onClick={onBack} testId="explore-back-btn" />
        {hasDogs ? (
          <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 shadow-sm">
            <MapPinIcon size={14} />
            {locationLabel || "Sin ubicación"}
          </div>
        ) : (
          <button
            onClick={onAddDog}
            data-testid="explore-add-dog-chip"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5C5C] px-4 py-1.5 text-xs font-bold text-white shadow-sm transition active:translate-y-[1px]"
          >
            🐶 Agregar mascota
          </button>
        )}
      </div>

      {!hasDogs && (
        <button
          onClick={onAddDog}
          data-testid="explore-add-dog-cta"
          className="mb-5 flex w-full items-center gap-4 rounded-3xl bg-gradient-to-br from-[#FF5C5C] to-[#ff7a7a] p-5 text-left text-white shadow-[0_12px_30px_-12px_rgba(255,92,92,0.7)] transition active:translate-y-[1px]"
        >
          <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/20 text-3xl">🐶</span>
          <span className="min-w-0 flex-1">
            <span className="block text-lg font-black">Agrega a tu peludito</span>
            <span className="block text-sm text-white/90">
              Para encontrar parejas, unirte a actividades y participar en PawCita, primero registra a tu perro.
            </span>
            <span className="mt-2 inline-block rounded-full bg-white px-4 py-1.5 text-sm font-bold text-[#FF5C5C]">
              Agregar mi mascota
            </span>
          </span>
        </button>
      )}

      <SectionTitle eyebrow="Explora">
        Perros compatibles cerca de ti
      </SectionTitle>
      <p className="mt-2 text-sm text-slate-500 md:text-base">
        {myDogName
          ? `Explora huellitas responsables para ${myDogName}.`
          : "Explora huellitas responsables para tu perro."}
      </p>

      {/* Search + filters */}
      <div className="mt-5 space-y-3">
        <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <span aria-hidden="true" className="text-slate-400">🔎</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por raza o nombre"
            data-testid="explore-search-input"
            className="w-full bg-transparent text-sm outline-none"
          />
          <button
            onClick={() => setShowFilters((s) => !s)}
            data-testid="explore-toggle-filters"
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold transition ${
              showFilters
                ? "bg-[#0B2D4D] text-white"
                : "bg-slate-50 text-[#0B2D4D] hover:bg-slate-100"
            }`}
          >
            <span aria-hidden="true">⚙️</span> Filtros
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {sexOptions.map((s) => (
            <PillToggle
              key={s}
              active={sex === s}
              onClick={() => setSex(s)}
              testId={`filter-sex-${s.toLowerCase()}`}
            >
              {s === "Todos" ? "Todos" : s === "Hembra" ? "Hembras" : "Machos"}
            </PillToggle>
          ))}
          <PillToggle
            active={vaccinesOnly}
            onClick={() => setVaccinesOnly((v) => !v)}
            testId="filter-vaccines"
          >
            💉 Vacunas al día
          </PillToggle>
          <PillToggle
            active={pedigreeOnly}
            onClick={() => setPedigreeOnly((v) => !v)}
            testId="filter-pedigree"
          >
            🏅 Pedigrí
          </PillToggle>
          <PillToggle
            active={availableOnly}
            onClick={() => setAvailableOnly((v) => !v)}
            testId="filter-available"
          >
            🐾 Disponibles
          </PillToggle>
        </div>

        {showFilters && (
          <div className="grid gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1.5 block font-semibold text-[#0B2D4D]">Tamaño</span>
              <select
                value={size}
                onChange={(e) => setSize(e.target.value)}
                data-testid="filter-size-select"
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#67D5B5]"
              >
                {sizeOptions.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1.5 block font-semibold text-[#0B2D4D]">Distancia</span>
              <select
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                data-testid="filter-distance-select"
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#67D5B5]"
              >
                {distanceBuckets.map((b) => (
                  <option key={b.key} value={b.key}>{b.label}</option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      {/* List */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="explore-list">
        {filtered.length === 0 && (
          <Card className="col-span-full p-8 text-center">
            <p className="font-display text-xl font-black text-[#0B2D4D]">
              No encontramos perros con esos filtros
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Prueba ajustando la distancia o el sexo.
            </p>
          </Card>
        )}
        {filtered.map((dog) => {
          const alreadySent = sentRequests.some((r) => r.dog.id === dog.id);
          return (
            <DogCard
              key={dog.id}
              dog={dog}
              alreadySent={alreadySent}
              onOpen={() => onOpenDog(dog)}
              onSend={() => onSendRequest(dog)}
            />
          );
        })}
      </div>
    </div>
  );
}

function PillToggle({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition ${
        active
          ? "border-transparent bg-[#0B2D4D] text-white shadow-sm"
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
      }`}
    >
      {children}
    </button>
  );
}

function DogCard({ dog, onOpen, onSend, alreadySent }) {
  return (
    <Card className="overflow-hidden pc-rise" data-testid={`dog-card-${dog.id}`}>
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left"
        aria-label={`Abrir perfil de ${dog.name}`}
      >
        <div className="relative h-56 w-full overflow-hidden">
          <img
            src={dog.image || "/dog-placeholder.svg"}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
            alt={dog.name}
            className="h-full w-full object-cover transition duration-500 hover:scale-[1.03]"
          />
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-[#0B2D4D] shadow">
            <MapPinIcon size={12} />
            {dog.distance}
          </div>
          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-white">
            <div>
              <p className="font-display text-2xl font-black leading-tight drop-shadow">
                {dog.name}
              </p>
              <p className="text-sm font-semibold opacity-90">
                {dog.breed} · {dog.age} años · {dog.sex}
              </p>
            </div>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#FF5C5C] shadow">
              <HeartIcon filled size={18} />
            </span>
          </div>
        </div>
      </button>

      <div className="space-y-3 p-4">
        <div className="flex flex-wrap gap-2">
          <Chip tone="mint">
            {dog.vaccines ? "💉 Vacunas al día" : "Revisar vacunas"}
          </Chip>
          <Chip tone="blue">🐾 {dog.size}</Chip>
          {dog.temperament?.[0] && <Chip tone="navy">{dog.temperament[0]}</Chip>}
          {dog.pedigree === "Sí" && <Chip tone="yellow">🏅 Pedigrí</Chip>}
        </div>

        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button
            variant="outline"
            onClick={onOpen}
            testId={`dog-card-open-${dog.id}`}
            className="w-full px-3"
            title="Olfatear mejor"
          >
            <span aria-hidden="true">🐾</span>
            <span className="text-xs sm:text-sm">Olfatear mejor</span>
          </Button>
          <Button
            variant={alreadySent ? "mint" : "primary"}
            onClick={onSend}
            disabled={alreadySent}
            testId={`dog-card-send-${dog.id}`}
            className="w-full px-3"
            title={alreadySent ? "Movimiento de colita enviado" : "Enviar movimiento de colita"}
          >
            <span aria-hidden="true">🐾</span>
            <span className="text-xs sm:text-sm">
              {alreadySent ? "Movimiento de colita enviado" : "Enviar movimiento de colita"}
            </span>
          </Button>
        </div>
      </div>
    </Card>
  );
}
